import cn from 'classnames';
import { graphql, useFragment } from 'react-relay';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';
import { type FC, useRef, useState, useEffect } from 'react';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { LABEL_VALUE_MONITORING_GROUP_PDP } from 'dibs-tracking/src/prometheus/peformanceTracking.ts';
import { usePdpRespMarketplaceLayoutPrimaryContext } from '../PdpRespMarketplaceLayout/PdpRespMarketplaceLayoutPrimaryContextProvider.tsx';
import { usePdpSharedSimilarSoldItemsContext } from '../../PdpSharedSimilarSoldItems/PdpSharedSimilarSoldItemsContext/PdpSharedSimilarSoldItemsContext.tsx';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import { showPricingGuidance as showPricingGuidanceHelper } from 'dibs-pricing-guidance/exports/showPricingGuidance.ts';
import {
    PDP_ESTIMATE_SIMILAR_SOLD_TEST_NAME,
    PDP_SIMILAR_SOLD_ITEMS_MODULE_TEST_NAME,
} from '../../../constants/abTestsConstants.ts';
import { getShowPdpEstimateInTab } from '../../helpers/getPdpEstimateSimilarSold.ts';
import { trackPricingGuidanceRangeDisplay } from 'dibs-pricing-guidance/exports/pricingGuidanceTracking.ts';
import { useShouldShowSimilarSoldItems } from '../../hooks/useShouldShowSimilarSoldItems.ts';
import { useSimilarSoldItemsTracking } from '../../hooks/useSimilarSoldItemsTracking.ts';

// components
import { PdpSharedEstimateValueBadge } from '../../shared/PdpSharedEstimateValueBadge/PdpSharedEstimateValueBadge.tsx';
import { PdpSharedPriceDisplay } from '../../PdpSharedPriceDisplay/PdpSharedPriceDisplay.tsx';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { PdpSharedPromoCode } from '../../PdpSharedPromoCode/PdpSharedPromoCode.tsx';
import { PdpSharedUrgencyLazy } from '../../PdpSharedUrgency/PdpSharedUrgencyLazy.tsx';
import { SharedPerformanceTracker } from '../../../shared/SharedPerformanceTracker.tsx';
import { PdpRespSidebarBlurbs } from '../../PdpRespSidebarBlurbs/PdpRespSidebarBlurbs.tsx';
import { PdpSharedQuantitySelect } from '../../PdpSharedButtons/PdpSharedQuantitySelect.tsx';
import { PdpRespSwatchSelector } from '../../PdpRespSwatchSelector/PdpRespSwatchSelector.tsx';
import { PdpRespSidebarButtons } from '../../PdpRespSidebarButtons/PdpRespSidebarButtons.tsx';
import { PdpRespSidebarDetails } from '../../PdpRespSidebarDetails/PdpRespSidebarDetails.tsx';
import { PdpRespDealerChatLoader } from '../../PdpRespDealerChat/PdpRespDealerChatLoader.tsx';
import { PdpSharedDesignYourOwnPillLazy } from '../../PdpSharedDesignYourOwnPill/PdpSharedDesignYourOwnPillLazy.tsx';
import { PdpRespSharedTitle } from '../../respShared/PdpRespSharedTitle/PdpRespSharedTitle.tsx';
import { PricingGuidanceInfo } from 'dibs-pricing-guidance/exports/PricingGuidanceInfo.ts';
import { PdpSharedPageTracker } from '../../shared/PdpSharedPageTracker/PdpSharedPageTracker.tsx';
import { PdpRespIconBubblesWrapper } from '../../PdpRespIconBubbles/PdpRespIconBubblesWrapper.tsx';
import EstimatedValue from 'dibs-pricing-guidance/exports/EstimatedValue.ts';
import PdpSharedEstimateSimilarSoldRange from '../../PdpSharedEstimateSimilarSold/PdpSharedEstimateSimilarSoldRange.tsx';
import {
    PdpRespDetailTabs,
    useDetailTabsState,
} from '../../PdpRespDetailTabs/PdpRespDetailTabs.tsx';
import { PdpRespSimilarItemsLanding } from '../../PdpRespSimilarItems/PdpRespSimilarItemsLanding.tsx';
import { PdpRespSharedQrCodeLazy } from '../../respShared/PdpRespSharedQrCode/PdpRespSharedQrCodeLazy.tsx';
import { PdpSharedPrivateOfferBanner } from '../../PdpSharedPrivateOfferBanner/PdpSharedPrivateOfferBanner.tsx';
import { PdpRespSharedBreadcrumbs } from '../../respShared/PdpRespSharedBreadcrumbs/PdpRespSharedBreadcrumbs.tsx';
import { PdpRespSharedHeroCarousel } from '../../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel.tsx';
import { PdpRespHeroCarousel } from '../PdpRespHeroCarousel/PdpRespHeroCarousel.tsx';
import { PdpRespMarketplaceStickyHeader } from '../PdpRespMarketplaceStickyHeader/PdpRespMarketplaceStickyHeader.tsx';
import { PdpRespSidebarSellerInfoAndContactLazy } from '../../PdpRespSidebarSellerInfo/PdpRespSidebarSellerInfoAndContactLazy.tsx';
import { PdpRespMarketplaceStickyHeaderSwitch } from '../PdpRespMarketplaceStickyHeader/PdpRespMarketplaceStickyHeaderSwitch.tsx';
import { useTrackSellerBrandingTest } from '../../helpers/sellerBrandingTestHelpers.ts';
import { useTrackTrustMessagingTest2 } from '../../helpers/trustMessagingTest2Helpers.ts';

// types
import { type PdpRespMarketplaceContainerPrimary_viewer$key as Viewer } from './__generated__/PdpRespMarketplaceContainerPrimary_viewer.graphql.ts';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpRespMarketplaceContainerPrimary.scss';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

// lazy components
const PdpRespPrintHeader = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespPrintHeader" */ '../../PdpRespPrintHeader/PdpRespPrintHeader.jsx'
        )
);

const PdpSharedItemFavorites = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedItemFavorites" */ '../../shared/PdpSharedItemFavorites/PdpSharedItemFavorites.tsx'
        )
);

const PdpSharedRestrictedMaterials = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedRestrictedMaterials" */ '../../shared/PdpSharedRestrictedMaterials/PdpSharedRestrictedMaterials.tsx'
        )
);

const PdpRespMarketplaceNotifications = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespMarketplaceNotifications" */ '../PdpRespMarketplaceNotifications/PdpRespMarketplaceNotifications.tsx'
        )
);

const PdpRespSharedPrintDetails = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespSharedPrintDetails" */ '../../respShared/PdpRespSharedPrintDetails/PdpRespSharedPrintDetails.tsx'
        )
);

const MerchandizingTool = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "MerchandizingTool" */ 'dibs-merchandizing-tools/exports/MerchandizingTool.ts'
        )
);

const PdpSharedRegistrationPromo = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedRegistrationPromo" */ '../../shared/PdpSharedRegistrationPromo/PdpSharedRegistrationPromo.tsx'
        )
);

const viewerFragment = graphql`
    fragment PdpRespMarketplaceContainerPrimary_viewer on Viewer {
        ...PdpRespDetailTabs_viewer @skip(if: $isPdpSiderailSellerReviewCombinedVariant)
        ...PdpSharedPromoCode_viewer
        ...PdpRespSharedHeroCarousel_viewer
        ...PdpRespHeroCarousel_viewer @include(if: $isPdpImageGalleryRedesignVariant)
        ...PdpSharedImageGalleryModal_viewer @include(if: $isPdpImageGalleryRedesignVariant)
        ...PdpRespIconBubblesWrapper_viewer
        ...PdpSharedPageTracker_viewer @include(if: $afterDisplay)
        ...PdpRespDealerChatLoader_viewer @include(if: $afterDisplay)
        ...PdpSharedRestrictedMaterials_refactor_viewer @include(if: $afterDisplay)
        ...PdpRespSidebarDetails_viewer @include(if: $isPdpSiderailSellerReviewCombinedVariant)
        ...PdpRespSidebarButtons_viewer @include(if: $afterDisplayOrBtSsr)
        ... @include(if: $isEstimateSimilarSoldVariant) {
            ...getPdpEstimateSimilarSold_viewer @include(if: $afterDisplay)
        }
        ...PdpSharedRegistrationPromo_viewer @include(if: $afterDisplay)
        ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
            ...PdpSharedEstimateValueBadge_viewer @include(if: $afterDisplayOrBtSsr)
        }
        ...useShouldShowSimilarSoldItems_viewer
        ...useSimilarSoldItemsTracking_viewer
        ...PdpRespSidebarSellerInfoAndContactLazy_viewer @include(if: $afterDisplay)
        itemRead(itemIds: $itemIds) {
            ... on Item {
                itemIsOnHold: isOnHold(buyerId: $userId)
                seller {
                    isAccessible
                    ...PdpRespSidebarSellerInfoAndContactLazy_seller @include(if: $afterDisplay)
                }
                displayInfo(page: pdp, buyerId: $userId) {
                    ctas {
                        showAcceptOffer
                    }
                    ...PdpSharedPromoCode_displayInfo
                    ...PdpRespSidebarBlurbs_displayInfo @include(if: $afterDisplay)
                    ...useDisplayInfo_displayInfo @include(if: $afterDisplayOrBtSsr)
                    ...PdpRespMarketplaceStickyHeader_displayInfo
                        @include(if: $isPdpSiderailSellerReviewCombinedVariant)
                }
                skus {
                    serviceId
                    ...PdpRespSidebarBlurbs_sku @include(if: $afterDisplay)
                    ...PdpRespMarketplaceStickyHeader_sku
                        @include(if: $isPdpSiderailSellerReviewCombinedVariant)
                }
                ...PdpSharedPriceDisplay_item @include(if: $afterDisplayOrBtSsr)
                ...PdpRespDetailTabs_item @skip(if: $isPdpSiderailSellerReviewCombinedVariant)
                ...PdpRespSharedTitle_item
                ...PdpSharedDesignYourOwnPillLazy_item
                ...EstimatedValue_item @include(if: $afterDisplay)
                ...PdpRespSharedBreadcrumbs_item
                ...PdpRespIconBubblesWrapper_item
                ...PdpRespSharedHeroCarousel_item
                ...PdpRespHeroCarousel_item @include(if: $isPdpImageGalleryRedesignVariant)
                ...PdpSharedImageGalleryModal_item @include(if: $isPdpImageGalleryRedesignVariant)
                ...PdpRespSimilarItemsLanding_item
                ...PricingGuidanceInfo_item @include(if: $afterDisplay)
                ...PdpRespSwatchSelector_item @include(if: $isMultiSku)
                ...PdpRespSidebarBlurbs_item @include(if: $afterDisplay)
                ...PdpSharedItemFavorites_item @include(if: $afterDisplay)
                ...PdpRespSharedPrintDetails_item @include(if: $afterDisplay)
                ...PdpRespSidebarButtons_item @include(if: $afterDisplayOrBtSsr)
                ...PdpRespMarketplaceNotifications_item @include(if: $afterDisplay)
                ...PdpSharedQuantitySelect_item @include(if: $afterDisplayOrBtSsr)
                ...PdpRespSidebarSellerInfoAndContactLazy_item @include(if: $afterDisplay)
                ...PdpRespSidebarDetails_item
                    @include(if: $isPdpSiderailSellerReviewCombinedVariant)
                ...PdpRespMarketplaceStickyHeader_item
                    @include(if: $isPdpSiderailSellerReviewCombinedVariant)
                ... @include(if: $isEstimateSimilarSoldVariant) {
                    ...PdpSharedEstimateSimilarSoldRange_item @include(if: $afterDisplay)
                    ...getPdpEstimateSimilarSold_item @include(if: $afterDisplay)
                }
                ...showPricingGuidance_item @include(if: $afterDisplay)
                ...pricingGuidanceTracking_item @include(if: $afterDisplay)
                ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
                    ...PdpSharedEstimateValueBadge_item @include(if: $afterDisplayOrBtSsr)
                }
                ...useShouldShowSimilarSoldItems_item
            }
        }
        user(userId: $userId) @include(if: $fetchUser) {
            hasMerchandizingPermission: hasPermission(name: "BUYER_FE_MERCH_TOOL")
            ...PdpRespDetailTabs_user @skip(if: $isPdpSiderailSellerReviewCombinedVariant)
            ...PdpSharedPromoCode_user
            ...PdpRespIconBubblesWrapper_user
            ...PdpRespSharedHeroCarousel_user
            ...PdpRespHeroCarousel_user @include(if: $isPdpImageGalleryRedesignVariant)
            ...PdpSharedImageGalleryModal_user @include(if: $isPdpImageGalleryRedesignVariant)
            ...PdpRespSimilarItemsLanding_user
            ...PdpRespSwatchSelector_user @include(if: $isMultiSku)
            ...PdpRespSidebarBlurbs_user @include(if: $afterDisplay)
            ...PdpSharedItemFavorites_user @include(if: $afterDisplay)
            ...PdpRespSharedPrintDetails_user @include(if: $afterDisplay)
            ...PdpRespSidebarButtons_user @include(if: $afterDisplayOrBtSsr)
            ...PdpRespMarketplaceNotifications_user @include(if: $afterDisplay)
            ...PdpRespSidebarSellerInfoAndContactLazy_user @include(if: $afterDisplay)
            ...PdpRespSidebarDetails_user @include(if: $isPdpSiderailSellerReviewCombinedVariant)
            ...PdpRespMarketplaceStickyHeader_user
                @include(if: $isPdpSiderailSellerReviewCombinedVariant)
        }
        similarLandingItems: moreLikeThisItem(
            first: $similarItemsCount
            itemId: $itemId
            isTrade: $isTrade
            useML: true
        ) {
            ...PdpRespSimilarItemsLanding_similarItems
        }
        ... @include(if: $afterDisplay) {
            buyerUrgencyInfo(itemId: $itemId, userId: $userId) {
                ...PdpSharedUrgencyLazy_buyerUrgencyInfo
                itemUrgency {
                    hasUrgencyInfo
                }
            }
        }
    }
`;

export const PdpRespMarketplaceContainerPrimary: FC<{ viewer: Viewer }> = ({
    viewer: viewerRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const detailsSectionRef = useRef<HTMLDivElement>(null);
    const similarItemsScrollRef = useRef<HTMLDivElement>(null);
    const scrollToCustomizationRef = useRef<HTMLDivElement>(null);
    const [itemQuantity, setItemQuantity] = useState<number | null>(null);
    const { onSkuChange, selectedSkuId, currency, printWithPrice, relayVariables } =
        usePdpRespMarketplaceLayoutPrimaryContext();
    const { onPdpRespEstimateValueBadgeClick } = usePdpSharedSimilarSoldItemsContext();
    const { trackSimilarSoldQualifyingPdpLoad, trackEstimateValueBadgeImpression } =
        useSimilarSoldItemsTracking({ viewer });
    const {
        isTablet,
        isPdpRespPreviewPage,
        isPdpEstimateSimilarSoldTestVariant,
        isPdpImageGalleryRedesignVariant,
        isItemFavoritingFeb2026Variant,
    } = useServerVarsContext();
    const {
        userId,
        itemId,
        isTrade,
        isMultiSku,
        afterDisplay,
        afterDisplayOrBtSsr,
        isPdpSiderailSellerReviewCombinedVariant = undefined,
    } = relayVariables?.variables || {};
    const { itemRead, user = null, similarLandingItems, buyerUrgencyInfo } = viewer;
    const item = itemRead?.[0] || null;
    const { hasUrgencyInfo } = buyerUrgencyInfo?.itemUrgency || {};

    const detailTabsState = useDetailTabsState();

    const selectedSku = item?.skus?.find(sku => sku?.serviceId === selectedSkuId) || null;

    const showPdpEstimateInTab =
        afterDisplay &&
        isPdpEstimateSimilarSoldTestVariant &&
        getShowPdpEstimateInTab({
            viewer: viewer || null,
            item,
            currency,
            isPdpEstimateSimilarSoldTestVariant: !!isPdpEstimateSimilarSoldTestVariant,
        });

    const showPricingGuidance = afterDisplay && showPricingGuidanceHelper({ item, currency });

    const {
        shouldShowEstimateValueBadge,
        areEligibleConditionsMet,
        isBelowMinPriceEstimate,
        isBelowOrEqualToMaxPriceEstimate,
    } = useShouldShowSimilarSoldItems({
        viewer,
        item,
        currency,
        afterDisplay,
    });

    useEffect(() => {
        if (areEligibleConditionsMet) {
            trackSimilarSoldQualifyingPdpLoad({
                isBelowMinPriceEstimate,
                isBelowOrEqualToMaxPriceEstimate,
            });
            trackAbTestV2Variant(PDP_SIMILAR_SOLD_ITEMS_MODULE_TEST_NAME);
        }
    }, [
        areEligibleConditionsMet,
        isBelowMinPriceEstimate,
        isBelowOrEqualToMaxPriceEstimate,
        trackSimilarSoldQualifyingPdpLoad,
    ]);

    useEffect(() => {
        if (showPricingGuidance) {
            trackAbTestV2Variant(PDP_ESTIMATE_SIMILAR_SOLD_TEST_NAME);
        }
    }, [showPricingGuidance]);

    useEffect(() => {
        if (afterDisplay && item) {
            trackPricingGuidanceRangeDisplay({ item });
        }
    }, [afterDisplay, item]);

    useTrackSellerBrandingTest();

    useTrackTrustMessagingTest2();

    if (!item) {
        return null;
    }

    const { itemIsOnHold, seller, displayInfo } = item;
    const { showAcceptOffer } = displayInfo?.ctas || {};
    const isSellerAccessible = !!seller?.isAccessible;
    const includePromoCode = !itemIsOnHold && isSellerAccessible;
    const showFavoriteInImageOverlay =
        afterDisplay && isPdpImageGalleryRedesignVariant && !isTablet;

    return (
        <>
            {afterDisplay && <PdpSharedPageTracker viewer={viewer} selectedSkuId={selectedSkuId} />}
            {afterDisplay && (
                <ClientSuspense fallback={null}>
                    <div className={styles.printShow}>
                        <PdpRespPrintHeader itemId={itemId} />
                    </div>
                </ClientSuspense>
            )}
            {afterDisplay && isPdpSiderailSellerReviewCombinedVariant && (
                <PdpRespMarketplaceStickyHeader
                    item={item}
                    user={user}
                    currency={currency}
                    displayInfo={displayInfo}
                    skuId={selectedSkuId || undefined}
                    sku={isMultiSku ? selectedSku : null}
                    itemQuantity={itemQuantity || undefined}
                />
            )}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsLanding
                    item={item}
                    user={user}
                    similarItems={similarLandingItems}
                    scrollToRef={similarItemsScrollRef}
                />
                <PdpRespSharedBreadcrumbs item={item} />
            </div>
            <div
                className={cn(
                    'rowFlex',
                    dibsCss.relative,
                    isPdpImageGalleryRedesignVariant &&
                        !isTablet &&
                        cn(
                            styles.pdpLargerHeroRow,
                            dibsCss.maxW1440px,
                            dibsCss.m0,
                            dibsCss.ptSmall,
                            dibsCss.px0,
                            dibsCss.pb0
                        )
                )}
                data-tn="pdp-main-container"
            >
                {/* Image Carousel */}
                <div className={cn('colXs12 colLg7 colXl8', dibsCss.ptSmall, styles.topContainer)}>
                    {isPdpImageGalleryRedesignVariant && !isTablet ? (
                        <PdpRespHeroCarousel
                            item={item}
                            user={user}
                            viewer={viewer}
                            currency={currency}
                            afterDisplay={!!afterDisplay}
                            selectedSkuId={selectedSkuId}
                            disableModals={isPdpRespPreviewPage}
                            enableSticky={!!isPdpSiderailSellerReviewCombinedVariant}
                            imageOverlay={
                                showFavoriteInImageOverlay ? (
                                    <ClientSuspense fallback={null}>
                                        <PdpSharedItemFavorites
                                            item={item}
                                            user={user}
                                            showHeartCount
                                            wrapperClass={dibsCss.contents}
                                            heartIconWrapperClass={dibsCss.mt0}
                                            folderIconWrapperClass={dibsCss.mt0}
                                            animateHeartPulse={false}
                                            showNudge={isItemFavoritingFeb2026Variant}
                                            theme="circle-frosted-large"
                                        />
                                    </ClientSuspense>
                                ) : undefined
                            }
                        >
                            <PdpRespIconBubblesWrapper
                                item={item}
                                user={user}
                                viewer={viewer}
                                skuId={selectedSkuId}
                                relayVariables={{
                                    isTrade: !!isTrade,
                                }}
                            />
                        </PdpRespHeroCarousel>
                    ) : (
                        <PdpRespSharedHeroCarousel
                            item={item}
                            user={user}
                            viewer={viewer}
                            currency={currency}
                            afterDisplay={!!afterDisplay}
                            selectedSkuId={selectedSkuId}
                            size={isTablet ? 'medium' : 'large'}
                            disableModals={isPdpRespPreviewPage}
                            enableImageGalleryModal={isPdpImageGalleryRedesignVariant}
                            isPdpSiderailSellerReviewCombinedVariant={
                                isPdpSiderailSellerReviewCombinedVariant
                            }
                        >
                            <PdpRespIconBubblesWrapper
                                item={item}
                                user={user}
                                viewer={viewer}
                                skuId={selectedSkuId}
                                relayVariables={{
                                    isTrade: !!isTrade,
                                    isPdpSiderailSellerReviewCombinedVariant,
                                }}
                            />
                        </PdpRespSharedHeroCarousel>
                    )}
                </div>
                {/* Sidebar */}
                <div
                    className={cn(
                        'colXs12 colLg5 colXl4',
                        styles.topContainer,
                        styles.sidebarContainer,
                        dibsCss.ptSmall,
                        dibsCss.pxLarge,
                        dibsCss.pb0,
                        {
                            [styles.isPdpSiderailSellerReviewCombinedVariant]:
                                isPdpSiderailSellerReviewCombinedVariant,
                        }
                    )}
                >
                    <div className={'rowFlex'}>
                        <div
                            className={`colXs12 ${
                                showFavoriteInImageOverlay ? 'colLg12' : 'colLg10'
                            }`}
                        >
                            <div className={styles.urgencyPlaceholder}>
                                {afterDisplay && buyerUrgencyInfo && hasUrgencyInfo && (
                                    <PdpSharedUrgencyLazy buyerUrgencyInfo={buyerUrgencyInfo} />
                                )}
                                {afterDisplay && !hasUrgencyInfo && (
                                    <PdpSharedDesignYourOwnPillLazy
                                        item={item}
                                        scrollToCustomizationRef={scrollToCustomizationRef}
                                    />
                                )}
                            </div>
                            <PdpRespSharedTitle
                                item={item}
                                isPdpSiderailSellerReviewCombinedVariant={
                                    isPdpSiderailSellerReviewCombinedVariant
                                }
                            />
                        </div>
                        {/* Favorite / Folder */}
                        {!showFavoriteInImageOverlay && (
                            <div className={cn('colXs2', styles.printHide)}>
                                {afterDisplay && (
                                    <ClientSuspense fallback={null}>
                                        <PdpSharedItemFavorites
                                            item={item}
                                            user={user}
                                            showHeartCount
                                            wrapperClass={cn(
                                                dibsCss.mt0,
                                                dibsCss.top0,
                                                dibsCss.absolute,
                                                dibsCss.contents,
                                                dibsCss.rightXsmall
                                            )}
                                            heartIconWrapperClass={dibsCss.mt0}
                                        />
                                    </ClientSuspense>
                                )}
                            </div>
                        )}

                        <div className={cn(dibsCss.wFull, dibsCss.pxXsmall)}>
                            {afterDisplay && showAcceptOffer && <PdpSharedPrivateOfferBanner />}
                            <div className={cn({ [styles.printHide]: !printWithPrice })}>
                                {afterDisplay && (
                                    <SharedPerformanceTracker
                                        datadogTimer={{ name: 'pdp_time_to_price' }}
                                        measureFromPageInit={{
                                            subGroup: 'resp/price-box',
                                            monitoringGroup: LABEL_VALUE_MONITORING_GROUP_PDP,
                                        }}
                                    />
                                )}
                                {isPdpSiderailSellerReviewCombinedVariant && (
                                    <PdpRespMarketplaceStickyHeaderSwitch />
                                )}
                                {afterDisplayOrBtSsr && (
                                    <>
                                        <PdpSharedPriceDisplay
                                            item={item}
                                            currency={currency}
                                            selectedSkuId={selectedSkuId}
                                            rightDecorator={
                                                shouldShowEstimateValueBadge ? (
                                                    <PdpSharedEstimateValueBadge
                                                        viewer={viewer}
                                                        item={item}
                                                        currency={currency}
                                                        onClick={onPdpRespEstimateValueBadgeClick}
                                                    />
                                                ) : undefined
                                            }
                                        />
                                        {areEligibleConditionsMet &&
                                            isBelowOrEqualToMaxPriceEstimate && (
                                                <VisibilityTracker
                                                    onVisibilityChange={({ isVisible }) => {
                                                        if (isVisible) {
                                                            trackEstimateValueBadgeImpression({
                                                                belowEstimate:
                                                                    isBelowMinPriceEstimate,
                                                            });
                                                        }
                                                    }}
                                                />
                                            )}
                                    </>
                                )}
                                {afterDisplay && isPdpEstimateSimilarSoldTestVariant && (
                                    <PdpSharedEstimateSimilarSoldRange
                                        item={item}
                                        currency={currency}
                                        estimatePricingRef={
                                            showPdpEstimateInTab
                                                ? scrollToCustomizationRef
                                                : similarItemsScrollRef
                                        }
                                    />
                                )}
                                {afterDisplay && !isPdpEstimateSimilarSoldTestVariant && (
                                    <EstimatedValue
                                        item={item}
                                        currency={currency}
                                        infoContent={
                                            <PricingGuidanceInfo
                                                item={item}
                                                iconClassName={dibsCss.fillSatan}
                                                trigger="siderail"
                                            />
                                        }
                                        textClassName={dibsCss.sassyFontBodyXSmallHeavy}
                                    />
                                )}
                                {includePromoCode && (
                                    <PdpSharedPromoCode
                                        displayInfo={displayInfo}
                                        itemId={itemId}
                                        viewer={viewer}
                                        user={user}
                                        userId={userId}
                                        currency={currency}
                                        excludeShippingPromos
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {isMultiSku && (
                        <PdpRespSwatchSelector
                            item={item}
                            user={user}
                            currency={currency}
                            isClient={afterDisplay}
                            showSwatchDescriptionOnHover
                            selectedSkuId={selectedSkuId}
                            onClick={(skuId: string) => onSkuChange(skuId)}
                        />
                    )}
                    {/* Item Ctas */}
                    {afterDisplayOrBtSsr && (
                        <div
                            className={cn(
                                dibsCss.mtSmall,
                                dibsCss.mbXxsmall,
                                dibsCss.mx0,
                                styles.printHide,
                                {
                                    [dibsCss.mbXsmall]: isPdpSiderailSellerReviewCombinedVariant,
                                }
                            )}
                        >
                            <PdpSharedQuantitySelect
                                item={item}
                                selectedSkuId={selectedSkuId}
                                setItemQuantity={setItemQuantity}
                            >
                                {quantitySelected => (
                                    <PdpRespSidebarButtons
                                        viewer={viewer}
                                        user={user}
                                        item={item}
                                        itemSearch={null}
                                        relayVariables={{
                                            itemId,
                                            userId,
                                            isMultiSku: isMultiSku || undefined,
                                        }}
                                        displayInfo={displayInfo}
                                        skuId={selectedSkuId || undefined}
                                        quantitySelected={quantitySelected || undefined}
                                        currency={currency}
                                    />
                                )}
                            </PdpSharedQuantitySelect>
                        </div>
                    )}

                    {/* Registration Promo */}
                    {afterDisplay && (
                        <ClientSuspense fallback={null}>
                            <PdpSharedRegistrationPromo viewer={viewer} />
                        </ClientSuspense>
                    )}

                    {/* Restricted Materials */}
                    {afterDisplay && (
                        <ClientSuspense fallback={null}>
                            <PdpSharedRestrictedMaterials viewer={viewer} />
                        </ClientSuspense>
                    )}
                    {/* Blurbs */}
                    {afterDisplay && isSellerAccessible && (
                        <>
                            <PdpRespSidebarBlurbs
                                item={item}
                                user={user}
                                currency={currency}
                                displayInfo={displayInfo}
                                sku={selectedSku || null}
                                itemQuantity={itemQuantity || undefined}
                                isPdpSiderailSellerReviewCombinedVariant={
                                    isPdpSiderailSellerReviewCombinedVariant || false
                                }
                                contactSellerComponent={
                                    isPdpSiderailSellerReviewCombinedVariant ? (
                                        <PdpRespSidebarSellerInfoAndContactLazy
                                            selectedSkuId={selectedSkuId || ''}
                                            viewer={viewer}
                                            user={user}
                                            seller={seller}
                                            item={item}
                                            itemQuantity={itemQuantity || undefined}
                                            currency={currency}
                                            isClient={afterDisplay}
                                        />
                                    ) : null
                                }
                            />
                            {afterDisplay && !isPdpSiderailSellerReviewCombinedVariant && (
                                <div
                                    className={cn(
                                        styles.printHide,
                                        dibsCss.ptSmall,
                                        dibsCss.borderT,
                                        dibsCss.borderTSolid,
                                        dibsCss.borderTDolphin
                                    )}
                                >
                                    {/* Questions about this piece? / Contact Dealer */}
                                    <PdpRespSidebarSellerInfoAndContactLazy
                                        viewer={viewer}
                                        user={user}
                                        item={item}
                                        seller={seller}
                                        currency={currency}
                                        isClient={afterDisplay}
                                        selectedSkuId={selectedSkuId || ''}
                                        itemQuantity={itemQuantity || undefined}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    {isPdpSiderailSellerReviewCombinedVariant && (
                        <>
                            {!afterDisplay && <div className={dibsCss.minH300px} />}
                            <PdpRespSidebarDetails
                                viewer={viewer}
                                item={item}
                                user={user}
                                skuId={selectedSkuId}
                                itemQuantity={itemQuantity}
                            />
                        </>
                    )}
                    <PdpRespSharedQrCodeLazy itemId={itemId} className={styles.printShow} />
                </div>
                {user?.hasMerchandizingPermission && userId && (
                    <ClientSuspense fallback={null}>
                        <div data-dibs-merchandizing-tool-item={itemId} />
                        <MerchandizingTool itemIds={[itemId]} userId={userId} />
                    </ClientSuspense>
                )}
            </div>
            <div ref={scrollToCustomizationRef} />
            <div
                ref={detailsSectionRef}
                className={cn(dibsCss.mtMedium, dibsCss.mbLarge, styles.details, styles.printHide)}
            >
                {!isPdpSiderailSellerReviewCombinedVariant && (
                    <PdpRespDetailTabs
                        item={item}
                        user={user}
                        viewer={viewer}
                        currency={currency}
                        itemQuantity={itemQuantity}
                        tabsState={detailTabsState}
                        selectedSkuId={selectedSkuId}
                    />
                )}
            </div>
            {afterDisplay && <PdpRespDealerChatLoader viewer={viewer} currency={currency} />}
            {afterDisplay && (
                <div className={styles.printShow}>
                    <ClientSuspense fallback={null}>
                        <PdpRespSharedPrintDetails
                            item={item}
                            user={user}
                            currency={currency}
                            selectedSkuId={selectedSkuId}
                        />
                    </ClientSuspense>
                </div>
            )}
            <div ref={similarItemsScrollRef} />

            {/* page notifications */}
            {afterDisplay && (
                <ErrorBoundary fallback={() => null}>
                    <ClientSuspense fallback={null}>
                        <PdpRespMarketplaceNotifications item={item} user={user} />
                    </ClientSuspense>
                </ErrorBoundary>
            )}
        </>
    );
};
